import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Alert,
	Backdrop,
	Box,
	Button,
	CircularProgress,
	Dialog,
	IconButton,
	Link,
	Snackbar,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { defaultAlertState } from 'features/common/constants';
import { ShowAlertState } from 'features/common/types';
import { getCurrencies, getSalesOrderId } from 'features/salesOrder/services';
import { ICodeName, ISaleOrderDetail, IServices, serviceTypes } from 'features/salesOrder/types';
import { getValidatingCarriers } from 'features/transactions/services';
import { IValidatingCarrier } from 'features/transactions/types';
import { extractErrorMessage } from 'features/quotation/helpers';
import { ServiceForm } from 'pages/SalesOrder/SalesOrderScreen/components/ServiceForm';
import { SaleOrderScopeProvider } from 'pages/SalesOrder/SalesOrderScreen/context/SaleOrderScopeContext';
import { ServiceQuickEditProvider } from 'pages/SalesOrder/SalesOrderScreen/context/ServiceQuickEditContext';
import { ProviderPayablesProvider } from 'pages/SalesOrder/SalesOrderScreen/context/ProviderPayablesContext';
import {
	computeServiceSaveImpact,
	ServiceSaveImpact,
} from 'pages/SalesOrder/SalesOrderScreen/helpers/serviceSaveImpact';
import { generateSalesOrderUrl } from 'pages/SalesOrder/SalesOrderScreen/helpers';

import { ServiceSaveConfirmDialog } from './ServiceSaveConfirmDialog';
import { QUICK_EDIT_SUPPORTED_TYPES } from './constants';

interface ServiceQuickEditDialogProps {
	open: boolean;
	saleOrderId: string;
	serviceId: string;
	agencyId?: number;
	onClose: () => void;
	onSaved: () => void;
}

export const ServiceQuickEditDialog = ({
	open,
	saleOrderId,
	serviceId,
	agencyId,
	onClose,
	onSaved,
}: ServiceQuickEditDialogProps): JSX.Element => {
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

	const [loading, setLoading] = useState(false);
	const [salesOrder, setSalesOrder] = useState<ISaleOrderDetail | null>(null);
	const [service, setService] = useState<IServices | null>(null);
	const [originalServiceSnapshot, setOriginalServiceSnapshot] = useState<Record<string, unknown> | null>(null);
	const [currencyesList, setCurrencyesList] = useState<ICodeName[]>([]);
	const [validatingCarriers, setValidatingCarriers] = useState<IValidatingCarrier[]>([]);
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [providerPayablesRefreshKey, setProviderPayablesRefreshKey] = useState(0);

	const [confirmOpen, setConfirmOpen] = useState(false);
	const [confirmImpact, setConfirmImpact] = useState<ServiceSaveImpact | null>(null);
	const confirmResolverRef = useRef<((value: boolean) => void) | null>(null);

	const loadData = useCallback(async () => {
		if (!saleOrderId || !serviceId) return;

		try {
			setLoading(true);
			setLoadError(null);

			const [salesOrderResponse, currenciesResponse, carriersResponse] = await Promise.all([
				getSalesOrderId(saleOrderId, { agencyId }),
				getCurrencies(),
				getValidatingCarriers(),
			]);

			const order = salesOrderResponse.data;
			const foundService = order.services?.find((item) => item.keyId === serviceId) ?? null;

			if (!foundService) {
				setLoadError('No se encontró el servicio en la orden de venta.');
				setSalesOrder(null);
				setService(null);
				return;
			}

			setSalesOrder(order);
			setService(foundService);
			setOriginalServiceSnapshot(structuredClone(foundService) as Record<string, unknown>);
			setCurrencyesList(currenciesResponse.data);
			setValidatingCarriers(carriersResponse.data);
			setProviderPayablesRefreshKey((prev) => prev + 1);
		} catch (error) {
			setLoadError(extractErrorMessage(error, 'Error al cargar el servicio'));
			setSalesOrder(null);
			setService(null);
		} finally {
			setLoading(false);
		}
	}, [saleOrderId, serviceId, agencyId]);

	useEffect(() => {
		if (!open) {
			setSalesOrder(null);
			setService(null);
			setOriginalServiceSnapshot(null);
			setLoadError(null);
			setAlert(defaultAlertState);
			return;
		}

		loadData();
	}, [open, loadData]);

	const handleClose = () => {
		if (confirmOpen) return;
		onClose();
	};

	const handleSaveSuccess = useCallback(() => {
		onSaved();
		onClose();
	}, [onClose, onSaved]);

	const requestConfirmation = useCallback(
		(updatedPayload: Record<string, unknown>): Promise<boolean> => {
			if (!salesOrder || !originalServiceSnapshot) {
				return Promise.resolve(true);
			}

			const impact = computeServiceSaveImpact(salesOrder, originalServiceSnapshot, updatedPayload);
			setConfirmImpact(impact);
			setConfirmOpen(true);

			return new Promise<boolean>((resolve) => {
				confirmResolverRef.current = resolve;
			});
		},
		[salesOrder, originalServiceSnapshot],
	);

	const resolveConfirmation = (confirmed: boolean) => {
		setConfirmOpen(false);
		setConfirmImpact(null);
		confirmResolverRef.current?.(confirmed);
		confirmResolverRef.current = null;
	};

	const handleServiceUpdate = useCallback(async () => {
		await loadData();
	}, [loadData]);

	const quickEditContextValue = useMemo(() => {
		if (!salesOrder || !service || !originalServiceSnapshot) return null;

		return {
			salesOrder,
			originalService: service,
			agencyId,
			requestConfirmation,
			onSaveSuccess: handleSaveSuccess,
		};
	}, [salesOrder, service, originalServiceSnapshot, agencyId, requestConfirmation, handleSaveSuccess]);

	const serviceTypeLabel =
		serviceTypes.find((item) => item.id === service?.serviceType)?.name ?? service?.serviceType ?? '';
	const serviceLabel = service ? `${serviceTypeLabel} · ${service.reference || service.keyId}` : 'Servicio';

	const isSupported = service ? QUICK_EDIT_SUPPORTED_TYPES.has(service.serviceType) : false;

	return (
		<>
			<Dialog open={open} onClose={handleClose} fullScreen={fullScreen} maxWidth="xl" fullWidth scroll="paper">
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						px: 3,
						py: 2,
						borderBottom: '1px solid',
						borderColor: 'divider',
						position: 'sticky',
						top: 0,
						zIndex: 2,
						backgroundColor: 'background.paper',
					}}
				>
					<Box>
						<Typography variant="h6" fontWeight={600}>
							Edición rápida de servicio
						</Typography>
						<Typography variant="body2" color="text.secondary">
							SaleOrder{' '}
							<Link
								href={generateSalesOrderUrl(saleOrderId, serviceId, agencyId)}
								target="_blank"
								rel="noopener noreferrer"
							>
								{saleOrderId}
							</Link>
							{service?.reference ? ` · ${service.reference}` : ''}
						</Typography>
					</Box>
					<Box display="flex" alignItems="center" gap={1}>
						<Button
							size="small"
							variant="outlined"
							startIcon={<OpenInNewIcon />}
							href={generateSalesOrderUrl(saleOrderId, serviceId, agencyId)}
							target="_blank"
							rel="noopener noreferrer"
						>
							Abrir en SaleOrder
						</Button>
						<IconButton onClick={handleClose} aria-label="Cerrar">
							<CloseIcon />
						</IconButton>
					</Box>
				</Box>

				<Box sx={{ position: 'relative', minHeight: 320, px: { xs: 1, md: 2 }, py: 2 }}>
					<Backdrop open={loading} sx={{ position: 'absolute', zIndex: 3, color: '#fff' }}>
						<CircularProgress color="inherit" />
					</Backdrop>

					{loadError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{loadError}
						</Alert>
					)}

					{service && !isSupported && (
						<Alert severity="info">
							Este tipo de servicio ({service.serviceType}) debe editarse desde la orden de venta.{' '}
							<Link
								href={generateSalesOrderUrl(saleOrderId, serviceId, agencyId)}
								target="_blank"
								rel="noopener noreferrer"
							>
								Abrir SaleOrder
							</Link>
						</Alert>
					)}

					{service && salesOrder && isSupported && quickEditContextValue && (
						<SaleOrderScopeProvider saleOrderId={saleOrderId}>
							<ServiceQuickEditProvider value={quickEditContextValue}>
								<ProviderPayablesProvider saleOrderId={saleOrderId} refreshKey={providerPayablesRefreshKey}>
									<ServiceForm
										service={service}
										serviceIndex={0}
										passengers={salesOrder.passengers ?? []}
										validatingCarriers={validatingCarriers}
										currencyesList={currencyesList}
										isExistingService
										isEnabled={service.enable !== false}
										onUpdateService={handleServiceUpdate}
										onSetAlert={setAlert}
									/>
								</ProviderPayablesProvider>
							</ServiceQuickEditProvider>
						</SaleOrderScopeProvider>
					)}
				</Box>
			</Dialog>

			<ServiceSaveConfirmDialog
				open={confirmOpen}
				impact={confirmImpact}
				serviceLabel={serviceLabel}
				onCancel={() => resolveConfirmation(false)}
				onConfirm={() => resolveConfirmation(true)}
			/>

			<Snackbar open={alert.show} autoHideDuration={3000} onClose={() => setAlert(defaultAlertState)}>
				<Alert variant="filled" severity={alert.severity} onClose={() => setAlert(defaultAlertState)}>
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};
